import type { NameFr } from "./drawings.fr";

// Dutch text for the drawings, laid over the English data (same pattern as
// drawings.fr.ts). Hints are keyed by the EXACT English string, so a line shared
// across a subject's Easy/Medium/Harder variants translates once.
// Force rebuild 2026-07-27
//
// Dutch grammar notes:
// - The indefinite article is always "een", so no sentence builder needs gender.
// - Nouns still carry their class: "m" = de-word (common), "n" = het-word
//   (neuter). Dutch merged m/f into one common gender, so "f" is unused here.
//   The class documents the noun and matches what rewards.nl.ts uses for
//   "de "/"het " on the sticker shelf.
// - Flag titles are "<Dutch demonym> vlag" — vlag is a de-word (titleGender
//   "m"), and nationality adjectives are always capitalised in Dutch
//   ("Nederlandse vlag"), which is exactly right mid-sentence:
//   "Zo teken je een Nederlandse vlag".
// - The "write the name" finale is NOT in HINTS_NL — it is redrawn from the
//   Dutch name by localize.ts (nameHintNl builds its spoken hint). Long names
//   get a shorter `written` form, exactly like the English Rain Cloud writes
//   just "CLOUD".

/** Drawing id → its Dutch name. */
export const NAMES_NL: Record<string, NameFr> = {
  "bear": { name: "Beer", gender: "m" },
  "bunny": { name: "Konijn", gender: "n" },
  "butterfly": { name: "Vlinder", gender: "m" },
  "butterfly-10": { name: "Vlinder", gender: "m" },
  "butterfly-7": { name: "Vlinder", gender: "m" },
  "car": { name: "Auto", gender: "m" },
  "car-10": { name: "Auto", gender: "m" },
  "car-7": { name: "Auto", gender: "m" },
  "cat": { name: "Kat", gender: "m" },
  "cat-10": { name: "Kat", gender: "m" },
  "cat-7": { name: "Kat", gender: "m" },
  "cloud": { name: "Regenwolk", gender: "m", written: "Wolk" },
  "cloud-10": { name: "Regenwolk", gender: "m", written: "Wolk" },
  "cloud-7": { name: "Regenwolk", gender: "m", written: "Wolk" },
  "cow": { name: "Koe", gender: "m" },
  "cubist-face": { name: "Kubistisch Gezicht", gender: "n" },
  "dinosaur": { name: "Dinosaurus", gender: "m" },
  "dinosaur-10": { name: "Dinosaurus", gender: "m" },
  "dinosaur-7": { name: "Dinosaurus", gender: "m" },
  "dinosaur-blue": { name: "Dinosaurus", gender: "m" },
  "dog": { name: "Hond", gender: "m" },
  "dog-10": { name: "Hond", gender: "m" },
  "dog-7": { name: "Hond", gender: "m" },
  "duck": { name: "Eend", gender: "m" },
  "elephant": { name: "Olifant", gender: "m" },
  "family": { name: "Gezin", gender: "n" },
  "family-10": { name: "Gezin", gender: "n" },
  "family-7": { name: "Gezin", gender: "n" },
  "fish": { name: "Vis", gender: "m" },
  "fish-10": { name: "Vis", gender: "m" },
  "fish-7": { name: "Vis", gender: "m" },
  "flag-algeria": { name: "Algerije", gender: "n", titleGender: "m" },
  "flag-argentina": { name: "Argentinië", gender: "n", titleGender: "m" },
  "flag-australia": { name: "Australië", gender: "n", titleGender: "m" },
  "flag-austria": { name: "Oostenrijk", gender: "n", titleGender: "m" },
  "flag-belgium": { name: "België", gender: "n", titleGender: "m" },
  "flag-bosnia-and-herzegovina": { name: "Bosnië en Herzegovina", gender: "n", titleGender: "m" },
  "flag-brazil": { name: "Brazilië", gender: "n", titleGender: "m" },
  "flag-canada": { name: "Canada", gender: "n", titleGender: "m" },
  "flag-cape-verde": { name: "Kaapverdië", gender: "n", titleGender: "m" },
  "flag-china": { name: "China", gender: "n", titleGender: "m" },
  "flag-colombia": { name: "Colombia", gender: "n", titleGender: "m" },
  "flag-croatia": { name: "Kroatië", gender: "n", titleGender: "m" },
  "flag-curacao": { name: "Curaçao", gender: "n", titleGender: "m" },
  "flag-czech-republic": { name: "Tsjechië", gender: "n", titleGender: "m" },
  "flag-denmark": { name: "Denemarken", gender: "n", titleGender: "m" },
  "flag-dr-congo": { name: "Congo-Kinshasa", gender: "n", titleGender: "m" },
  "flag-ecuador": { name: "Ecuador", gender: "n", titleGender: "m" },
  "flag-egypt": { name: "Egypte", gender: "n", titleGender: "m" },
  "flag-england": { name: "Engeland", gender: "n", titleGender: "m" },
  "flag-finland": { name: "Finland", gender: "n", titleGender: "m" },
  "flag-france": { name: "Frankrijk", gender: "n", titleGender: "m" },
  "flag-germany": { name: "Duitsland", gender: "n", titleGender: "m" },
  "flag-ghana": { name: "Ghana", gender: "n", titleGender: "m" },
  "flag-greece": { name: "Griekenland", gender: "n", titleGender: "m" },
  "flag-haiti": { name: "Haïti", gender: "n", titleGender: "m" },
  "flag-india": { name: "India", gender: "n", titleGender: "m" },
  "flag-iran": { name: "Iran", gender: "n", titleGender: "m" },
  "flag-iraq": { name: "Irak", gender: "n", titleGender: "m" },
  "flag-ireland": { name: "Ierland", gender: "n", titleGender: "m" },
  "flag-italy": { name: "Italië", gender: "n", titleGender: "m" },
  "flag-ivory-coast": { name: "Ivoorkust", gender: "m", titleGender: "m" },
  "flag-japan": { name: "Japan", gender: "n", titleGender: "m" },
  "flag-jordan": { name: "Jordanië", gender: "n", titleGender: "m" },
  "flag-mexico": { name: "Mexico", gender: "n", titleGender: "m" },
  "flag-morocco": { name: "Marokko", gender: "n", titleGender: "m" },
  "flag-netherlands": { name: "Nederland", gender: "n", titleGender: "m" },
  "flag-new-zealand": { name: "Nieuw-Zeeland", gender: "n", titleGender: "m" },
  "flag-norway": { name: "Noorwegen", gender: "n", titleGender: "m" },
  "flag-panama": { name: "Panama", gender: "n", titleGender: "m" },
  "flag-paraguay": { name: "Paraguay", gender: "n", titleGender: "m" },
  "flag-portugal": { name: "Portugal", gender: "n", titleGender: "m" },
  "flag-qatar": { name: "Qatar", gender: "n", titleGender: "m" },
  "flag-saudi-arabia": { name: "Saoedi-Arabië", gender: "n", titleGender: "m" },
  "flag-scotland": { name: "Schotland", gender: "n", titleGender: "m" },
  "flag-senegal": { name: "Senegal", gender: "n", titleGender: "m" },
  "flag-singapore": { name: "Singapore", gender: "n", titleGender: "m" },
  "flag-south-africa": { name: "Zuid-Afrika", gender: "n", titleGender: "m" },
  "flag-south-korea": { name: "Zuid-Korea", gender: "n", titleGender: "m" },
  "flag-spain": { name: "Spanje", gender: "n", titleGender: "m" },
  "flag-sweden": { name: "Zweden", gender: "n", titleGender: "m" },
  "flag-switzerland": { name: "Zwitserland", gender: "n", titleGender: "m" },
  "flag-tunisia": { name: "Tunesië", gender: "n", titleGender: "m" },
  "flag-turkey": { name: "Turkije", gender: "n", titleGender: "m" },
  "flag-united-arab-emirates": { name: "Verenigde Arabische Emiraten", gender: "m", titleGender: "m" },
  "flag-united-kingdom": { name: "Verenigd Koninkrijk", gender: "n", titleGender: "m" },
  "flag-united-states": { name: "Verenigde Staten", gender: "m", titleGender: "m" },
  "flag-uruguay": { name: "Uruguay", gender: "n", titleGender: "m" },
  "flag-uzbekistan": { name: "Oezbekistan", gender: "n", titleGender: "m" },
  "flower": { name: "Bloem", gender: "m" },
  "flower-10": { name: "Bloem", gender: "m" },
  "flower-7": { name: "Bloem", gender: "m" },
  "fox": { name: "Vos", gender: "m" },
  "frog": { name: "Kikker", gender: "m" },
  "great-wave": { name: "Grote Golf", gender: "m" },
  "hamster": { name: "Hamster", gender: "m" },
  "heart": { name: "Hart", gender: "n" },
  "heart-10": { name: "Hart", gender: "n" },
  "heart-7": { name: "Hart", gender: "n" },
  "hedgehog": { name: "Egel", gender: "m" },
  "hedgehog-10": { name: "Egel", gender: "m" },
  "hedgehog-7": { name: "Egel", gender: "m" },
  "heromask": { name: "Super-Stormy", gender: "m" },
  "heromask-10": { name: "Super-Stormy", gender: "m" },
  "heromask-7": { name: "Super-Stormy", gender: "m" },
  "horse": { name: "Paard", gender: "n" },
  "horse-10": { name: "Paard", gender: "n" },
  "horse-7": { name: "Paard", gender: "n" },
  "house": { name: "Huis", gender: "n" },
  "house-10": { name: "Huis", gender: "n" },
  "house-7": { name: "Huis", gender: "n" },
  "ice-cream": { name: "IJsje", gender: "n" },
  "jersey": { name: "Shirt", gender: "n" },
  "lion": { name: "Leeuw", gender: "m" },
  "mouse": { name: "Muis", gender: "m" },
  "penguin": { name: "Pinguïn", gender: "m" },
  "picasso-portrait": { name: "Picasso-Portret", gender: "n" },
  "pig": { name: "Varken", gender: "n" },
  "popstar": { name: "Popster", gender: "m" },
  "popstar-10": { name: "Popster", gender: "m" },
  "popstar-7": { name: "Popster", gender: "m" },
  "princess": { name: "Prinses", gender: "m" },
  "princess-10": { name: "Prinses", gender: "m" },
  "princess-7": { name: "Prinses", gender: "m" },
  "rainbow": { name: "Regenboog", gender: "m" },
  "rainbow-10": { name: "Regenboog", gender: "m" },
  "rainbow-7": { name: "Regenboog", gender: "m" },
  "shape-circle": { name: "Cirkel", gender: "m" },
  "shape-crescent": { name: "Halve Maan", gender: "m", written: "Maan" },
  "shape-diamond": { name: "Ruit", gender: "m" },
  "shape-heart": { name: "Hart", gender: "n" },
  "shape-hexagon": { name: "Zeshoek", gender: "m" },
  "shape-octagon": { name: "Achthoek", gender: "m" },
  "shape-oval": { name: "Ovaal", gender: "n" },
  "shape-parallelogram": { name: "Parallellogram", gender: "n" },
  "shape-pentagon": { name: "Vijfhoek", gender: "m" },
  "shape-rectangle": { name: "Rechthoek", gender: "m" },
  "shape-square": { name: "Vierkant", gender: "n" },
  "shape-star": { name: "Ster", gender: "m" },
  "shape-trapezium": { name: "Trapezium", gender: "n" },
  "shape-triangle": { name: "Driehoek", gender: "m" },
  "snail": { name: "Slak", gender: "m" },
  "star": { name: "Ster", gender: "m" },
  "star-10": { name: "Ster", gender: "m" },
  "star-7": { name: "Ster", gender: "m" },
  "starry-night": { name: "Sterrennacht", gender: "m" },
  "sun": { name: "Zon", gender: "m" },
  "sun-10": { name: "Zon", gender: "m" },
  "sun-7": { name: "Zon", gender: "m" },
  "tree": { name: "Boom", gender: "m" },
  "tree-10": { name: "Boom", gender: "m" },
  "tree-7": { name: "Boom", gender: "m" },
  "unicorn-10": { name: "Eenhoorn", gender: "m" },
  "whale": { name: "Walvis", gender: "m" },
};

/** English flag title → Dutch ("Franse vlag" — nationality adjectives stay
 *  capitalised in Dutch, and vlag is a de-word, so the heading reads
 *  "Zo teken je een Franse vlag"). */
export const TITLES_NL: Record<string, string> = {
  "Algerian flag": "Algerijnse vlag",
  "American flag": "Amerikaanse vlag",
  "Argentine flag": "Argentijnse vlag",
  "Australian flag": "Australische vlag",
  "Austrian flag": "Oostenrijkse vlag",
  "Belgian flag": "Belgische vlag",
  "Bosnian flag": "Bosnische vlag",
  "Brazilian flag": "Braziliaanse vlag",
  "British flag": "Britse vlag",
  "Canadian flag": "Canadese vlag",
  "Cape Verdean flag": "Kaapverdische vlag",
  "Chinese flag": "Chinese vlag",
  "Colombian flag": "Colombiaanse vlag",
  "Congolese flag": "Congolese vlag",
  "Croatian flag": "Kroatische vlag",
  "Curaçao flag": "Curaçaose vlag",
  "Czech flag": "Tsjechische vlag",
  "Danish flag": "Deense vlag",
  "Dutch flag": "Nederlandse vlag",
  "Ecuadorian flag": "Ecuadoraanse vlag",
  "Egyptian flag": "Egyptische vlag",
  "Emirati flag": "Emiratische vlag",
  "English flag": "Engelse vlag",
  "Finnish flag": "Finse vlag",
  "French flag": "Franse vlag",
  "German flag": "Duitse vlag",
  "Ghanaian flag": "Ghanese vlag",
  "Greek flag": "Griekse vlag",
  "Haitian flag": "Haïtiaanse vlag",
  "Indian flag": "Indiase vlag",
  "Iranian flag": "Iraanse vlag",
  "Iraqi flag": "Iraakse vlag",
  "Irish flag": "Ierse vlag",
  "Italian flag": "Italiaanse vlag",
  "Ivorian flag": "Ivoriaanse vlag",
  "Japanese flag": "Japanse vlag",
  "Jordanian flag": "Jordaanse vlag",
  "Mexican flag": "Mexicaanse vlag",
  "Moroccan flag": "Marokkaanse vlag",
  "New Zealand flag": "Nieuw-Zeelandse vlag",
  "Norwegian flag": "Noorse vlag",
  "Panamanian flag": "Panamese vlag",
  "Paraguayan flag": "Paraguayaanse vlag",
  "Portuguese flag": "Portugese vlag",
  "Qatari flag": "Qatarese vlag",
  "Saudi flag": "Saoedische vlag",
  "Scottish flag": "Schotse vlag",
  "Senegalese flag": "Senegalese vlag",
  "Singaporean flag": "Singaporese vlag",
  "South African flag": "Zuid-Afrikaanse vlag",
  "South Korean flag": "Zuid-Koreaanse vlag",
  "Spanish flag": "Spaanse vlag",
  "Swedish flag": "Zweedse vlag",
  "Swiss flag": "Zwitserse vlag",
  "Tunisian flag": "Tunesische vlag",
  "Turkish flag": "Turkse vlag",
  "Uruguayan flag": "Uruguayaanse vlag",
  "Uzbek flag": "Oezbeekse vlag",
};

/** English celebration fact → Dutch. Keyed by the exact English string. */
export const FACTS_NL: Record<string, string> = {
  "A circle is perfectly round — it has no corners at all! The sun and the moon are circles. ☀️":
    "Een cirkel is helemaal rond — hij heeft helemaal geen hoeken! De zon en de maan zijn cirkels. ☀️",
  "A crescent is the curvy shape the moon makes at night! 🌙":
    "Een halve maan is de kromme vorm die de maan 's nachts maakt! 🌙",
  "A diamond is a square balancing on one of its corners! 💎":
    "Een ruit is een vierkant dat op één van zijn hoeken balanceert! 💎",
  "A heart is the shape we use to show love! 💕":
    "Een hart is de vorm die we gebruiken om liefde te laten zien! 💕",
  "A hexagon has 6 sides — bees build their honeycomb out of hexagons! 🐝":
    "Een zeshoek heeft 6 zijden — bijen bouwen hun honingraat van zeshoeken! 🐝",
  "A parallelogram is like a rectangle that leaned over to one side!":
    "Een parallellogram is als een rechthoek die scheef naar één kant is gaan leunen!",
  "A pentagon has 5 straight sides — a little house is shaped a bit like one! 🏠":
    "Een vijfhoek heeft 5 rechte zijden — een huisje lijkt er een beetje op! 🏠",
  "A rectangle has 4 corners, with 2 long sides and 2 short sides — like a door! 🚪":
    "Een rechthoek heeft 4 hoeken, met 2 lange zijden en 2 korte zijden — net als een deur! 🚪",
  "A square has 4 sides that are all exactly the same length!":
    "Een vierkant heeft 4 zijden die allemaal precies even lang zijn!",
  "A star has 5 points that twinkle in the night sky! ✨":
    "Een ster heeft 5 punten die fonkelen aan de nachtelijke hemel! ✨",
  "A trapezium has 4 sides, with a flat top and a flat bottom that aren't the same width!":
    "Een trapezium heeft 4 zijden, met een platte bovenkant en een platte onderkant die niet even breed zijn!",
  "A triangle has 3 sides and 3 pointy corners!":
    "Een driehoek heeft 3 zijden en 3 puntige hoeken!",
  "Algeria's flag has a red moon and star.":
    "De vlag van Algerije heeft een rode maan en ster.",
  "An octagon has 8 sides — a red STOP sign is an octagon! 🛑":
    "Een achthoek heeft 8 zijden — een rood STOP-bord is een achthoek! 🛑",
  "An oval is like a circle that got gently squished — an egg is an oval! 🥚":
    "Een ovaal is als een cirkel die zachtjes is platgedrukt — een ei is een ovaal! 🥚",
  "Argentina's flag has a smiling golden sun.":
    "De vlag van Argentinië heeft een lachende gouden zon.",
  "Australia's flag shows the Southern Cross stars.":
    "De vlag van Australië laat de sterren van het Zuiderkruis zien.",
  "Austria's flag is red, white, red.":
    "De vlag van Oostenrijk is rood, wit, rood.",
  "Belgium's flag is black, yellow and red.":
    "De vlag van België is zwart, geel en rood.",
  "Bosnia's flag has a yellow triangle and white stars.":
    "De vlag van Bosnië heeft een gele driehoek en witte sterren.",
  "Brazil's flag has a starry blue globe.":
    "De vlag van Brazilië heeft een blauwe wereldbol vol sterren.",
  "Canada's flag shows a red maple leaf.":
    "De vlag van Canada laat een rood esdoornblad zien.",
  "Cape Verde's flag has ten yellow stars in a circle.":
    "De vlag van Kaapverdië heeft tien gele sterren in een cirkel.",
  "China's flag has one big star and four small ones.":
    "De vlag van China heeft één grote ster en vier kleintjes.",
  "Colombia's flag is yellow, blue and red.":
    "De vlag van Colombia is geel, blauw en rood.",
  "Croatia's flag has a red-and-white checkerboard.":
    "De vlag van Kroatië heeft een rood-wit dambord.",
  "Curaçao's flag has two white stars.":
    "De vlag van Curaçao heeft twee witte sterren.",
  "DR Congo's flag has a yellow star and a red stripe.":
    "De vlag van Congo-Kinshasa heeft een gele ster en een rode streep.",
  "Denmark's flag is the oldest in the world!":
    "De vlag van Denemarken is de oudste van de wereld!",
  "Ecuador's flag has a condor on a shield.":
    "De vlag van Ecuador heeft een condor op een schild.",
  "Egypt's flag has a golden eagle.":
    "De vlag van Egypte heeft een gouden adelaar.",
  "England's flag is a red cross.":
    "De vlag van Engeland is een rood kruis.",
  "Finland's flag has a blue cross.":
    "De vlag van Finland heeft een blauw kruis.",
  "France's flag is called the Tricolore.":
    "De vlag van Frankrijk heet de Tricolore.",
  "Germany's flag is black, red and gold.":
    "De vlag van Duitsland is zwart, rood en goud.",
  "Ghana's flag has a black star.":
    "De vlag van Ghana heeft een zwarte ster.",
  "Greece's flag has nine blue and white stripes.":
    "De vlag van Griekenland heeft negen blauwe en witte strepen.",
  "Haiti's flag has a palm tree in the middle.":
    "De vlag van Haïti heeft een palmboom in het midden.",
  "Hokusai made this in Japan almost 200 years ago — it's printed from carved wood!":
    "Hokusai maakte dit in Japan bijna 200 jaar geleden — het is gedrukt van uitgehakt hout!",
  "In real life, Switzerland's flag is a perfect square!":
    "In het echt is de vlag van Zwitserland een perfect vierkant!",
  "India's flag has a blue wheel in the middle.":
    "De vlag van India heeft een blauw wiel in het midden.",
  "Iran's flag has a red emblem in the middle.":
    "De vlag van Iran heeft een rood embleem in het midden.",
  "Iraq's flag has green writing in the middle.":
    "De vlag van Irak heeft groene letters in het midden.",
  "Ireland's flag is green, white and orange.":
    "De vlag van Ierland is groen, wit en oranje.",
  "Italy's flag is green, white and red.":
    "De vlag van Italië is groen, wit en rood.",
  "Ivory Coast's flag is orange, white and green.":
    "De vlag van Ivoorkust is oranje, wit en groen.",
  "Japan's flag shows the rising sun.":
    "De vlag van Japan laat de opkomende zon zien.",
  "Jordan's flag has a white star with seven points.":
    "De vlag van Jordanië heeft een witte ster met zeven punten.",
  "Mexico's flag has an eagle on a cactus.":
    "De vlag van Mexico heeft een adelaar op een cactus.",
  "Morocco's flag has a green star.":
    "De vlag van Marokko heeft een groene ster.",
  "New Zealand's flag has four red stars.":
    "De vlag van Nieuw-Zeeland heeft vier rode sterren.",
  "Norway's flag has a cross inside a cross.":
    "De vlag van Noorwegen heeft een kruis in een kruis.",
  "Panama's flag has a blue star and a red star.":
    "De vlag van Panama heeft een blauwe ster en een rode ster.",
  "Paraguay's flag has a star in the middle.":
    "De vlag van Paraguay heeft een ster in het midden.",
  "Picasso drew faces from the front and the side at the same time — that's Cubism!":
    "Picasso tekende gezichten tegelijk van voren en van de zijkant — dat is het kubisme!",
  "Picasso often showed a face from the front and the side at the same time — two views in one!":
    "Picasso liet een gezicht vaak tegelijk van voren en van de zijkant zien — twee kanten in één!",
  "Portugal's flag has a golden sphere and a shield.":
    "De vlag van Portugal heeft een gouden bol en een schild.",
  "Qatar's flag has nine white points.":
    "De vlag van Qatar heeft negen witte punten.",
  "Saudi Arabia's flag has a sword.":
    "De vlag van Saoedi-Arabië heeft een zwaard.",
  "Scotland's flag is a white X on blue.":
    "De vlag van Schotland is een witte X op blauw.",
  "Senegal's flag has a green star.":
    "De vlag van Senegal heeft een groene ster.",
  "Singapore's flag has a moon and five stars.":
    "De vlag van Singapore heeft een maan en vijf sterren.",
  "South Africa's flag has six colours in a Y shape.":
    "De vlag van Zuid-Afrika heeft zes kleuren in de vorm van een Y.",
  "South Korea's flag has a red-and-blue circle.":
    "De vlag van Zuid-Korea heeft een rood-blauwe cirkel.",
  "Spain's flag has a crown and a shield.":
    "De vlag van Spanje heeft een kroon en een schild.",
  "Sweden's flag has a yellow cross.":
    "De vlag van Zweden heeft een geel kruis.",
  "The Czech flag has a blue triangle.":
    "De Tsjechische vlag heeft een blauwe driehoek.",
  "The Netherlands flag is red, white and blue.":
    "De Nederlandse vlag is rood, wit en blauw.",
  "The UAE flag has four colours.":
    "De vlag van de VAE heeft vier kleuren.",
  "The USA flag has 13 stripes and 50 stars!":
    "De vlag van de VS heeft 13 strepen en 50 sterren!",
  "The Union Jack joins three crosses together.":
    "De Union Jack voegt drie kruisen samen.",
  "Tunisia's flag has a red moon and star in a white circle.":
    "De vlag van Tunesië heeft een rode maan en ster in een witte cirkel.",
  "Turkey's flag has a white moon and star.":
    "De vlag van Turkije heeft een witte maan en ster.",
  "Uruguay's flag has a golden sun and nine stripes.":
    "De vlag van Uruguay heeft een gouden zon en negen strepen.",
  "Uzbekistan's flag has a moon and stars.":
    "De vlag van Oezbekistan heeft een maan en sterren.",
  "Vincent van Gogh painted Starry Night from his window — with big swirly brushstrokes!":
    "Vincent van Gogh schilderde De sterrennacht vanuit zijn raam — met grote draaiende penseelstreken!",
};

export const HINTS_NL: Record<string, string> = {
  "Add 10 little stars in a circle ⭐": "Voeg 10 kleine sterren in een cirkel ⭐",
  "Add 13 stripes across": "Voeg 13 strepen toe overdwars",
  "Add 3 stripes across": "Voeg 3 strepen toe overdwars",
  "Add 3 stripes across the rest": "Voeg 3 strepen toe overdwars over de rest",
  "Add 3 stripes up and down": "Voeg 3 strepen toe van boven naar beneden",
  "Add 50 little stars ⭐": "Voeg 50 kleine sterren toe ⭐",
  "Add 9 stripes across": "Voeg 9 strepen toe overdwars",
  "Add a band across the bottom half": "Voeg een band over de onderste helft",
  "Add a big cloud of leaves 🌳": "Voeg een grote wolk van bladeren toe 🌳",
  "Add a big flowing cape!": "Voeg een grote wapperende cape toe!",
  "Add a big friendly smile 😄": "Voeg een grote vriendelijke glimlach toe 😄",
  "Add a big muzzle and a smile 🐽": "Voeg een grote snuit en een glimlach toe 🐽",
  "Add a big singing smile 🎶": "Voeg een grote zingende glimlach toe 🎶",
  "Add a big smile 😄": "Voeg een grote glimlach toe 😄",
  "Add a big snout 🐽": "Voeg een grote snuit 🐽",
  "Add a big zig-zag lightning bolt! ⚡": "Voeg een grote zig-zag bliksem bliksemschicht! ⚡",
  "Add a box in the corner": "Voeg een vak in de hoek",
  "Add a box in the corner for the stars": "Voeg een vak in de hoek voor de sterren",
  "Add a box in the corner for the sun": "Voeg een vak in de hoek voor de zon",
  "Add a brave smile": "Voeg een dappere glimlach toe",
  "Add a bright sun! ☀️": "Voeg een felle zon toe! ☀️",
  "Add a buzzy bee 🐝": "Voeg een zoemende bij toe 🐝",
  "Add a collar with a name tag": "Voeg een halsband met een naamplaatje toe",
  "Add a colourful umbrella ☂️": "Voeg een kleurrijke paraplu toe ☂️",
  "Add a crescent moon 🌙": "Voeg een halve maan toe 🌙",
  "Add a crooked smile 🙂": "Voeg een scheve glimlach toe 🙂",
  "Add a cross in the box ➕": "Voeg een kruis in het vak ➕",
  "Add a cross — a tall line and a wide line ➕": "Voeg een kruis — een hoge lijn en een brede lijn ➕",
  "Add a curly tail!": "Voeg een krullende staart!",
  "Add a curvy band 🎀": "Voeg een gebogen band toe 🎀",
  "Add a cute nose 👃": "Voeg een schattig neusje toe 👃",
  "Add a door, handle and headlight": "Voeg een deur, handvat en koplamp",
  "Add a fat white cross in the middle ➕": "Voeg een dik wit kruis in het midden toe ➕",
  "Add a fin on top and bottom": "Voeg een vin bovenop en onderaan toe",
  "Add a five-point star ⭐": "Voeg een vijfpuntige ster toe ⭐",
  "Add a flowing cape!": "Voeg een wapperende cape toe!",
  "Add a fluffy cloud at each end ☁️": "Voeg een donzige wolk aan beide kanten toe ☁️",
  "Add a fluffy cloud on the left ☁️": "Voeg een donzige wolk links toe ☁️",
  "Add a fluffy cloud ☁️": "Voeg een donzige wolk toe ☁️",
  "Add a glowing moon and twinkly stars 🌙": "Voeg een gloeiende maan en fonkelende sterren toe 🌙",
  "Add a happy eye 👀": "Voeg een blije oog 👀",
  "Add a hero belt": "Voeg een heldengordel toe",
  "Add a line across the middle": "Voeg een lijn overdwars de midden",
  "Add a line down": "Voeg een lijn naar beneden toe",
  "Add a line down for the red bar": "Voeg een lijn naar beneden voor de rode balk toe",
  "Add a line down the middle": "Voeg een lijn naar beneden in het midden toe",
  "Add a line of little stars ⭐": "Voeg een lijn of kleine sterren ⭐",
  "Add a little arm": "Voeg een klein armpje toe",
  "Add a little bird 🐦": "Voeg een klein vogeltje toe 🐦",
  "Add a little cluster of stars ⭐": "Voeg een klein groepje sterren toe ⭐",
  "Add a little feather tuft on top 🪶": "Voeg een klein plukje veren bovenop toe 🪶",
  "Add a little fence and flowers 🌷": "Voeg een kleine hek en bloemen 🌷",
  "Add a little nose 👃": "Voeg een kleine neus 👃",
  "Add a little orange beak 🔶": "Voeg een kleine oranje snavel 🔶",
  "Add a little tongue!": "Voeg een klein tongetje toe!",
  "Add a little triangle nose 👃": "Voeg een kleine driehoek neus 👃",
  "Add a long horse head from the side": "Voeg een lange paard hoofd van de zijde",
  "Add a long neck and a big head": "Voeg een lange nek en een grote hoofd",
  "Add a long swishy tail": "Voeg een lange zwiepende staart toe",
  "Add a long trunk 🐘": "Voeg een lange stam 🐘",
  "Add a magic wand! ✨": "Voeg een toverstaf toe! ✨",
  "Add a microphone to sing into! 🎤": "Voeg een microfoon om in te zingen toe! 🎤",
  "Add a microphone with a cord 🎤": "Voeg een microfoon met een snoer 🎤",
  "Add a nose and a smile 👃": "Voeg een neusje en een glimlach toe 👃",
  "Add a nose and a smile 😄": "Voeg een neusje en een glimlach toe 😄",
  "Add a nose seen from the side 👃": "Voeg een neus van opzij toe 👃",
  "Add a pointy ear 👂": "Voeg een puntige oor 👂",
  "Add a rosy cheek and a smile 😊": "Voeg een roze wang en een glimlach toe 😊",
  "Add a round face on top 🟡": "Voeg een rond gezichtje bovenop toe 🟡",
  "Add a second row of spikes": "Voeg een tweede rij stekels toe",
  "Add a second row of spikes!": "Voeg een tweede rij stekels toe!",
  "Add a shiny highlight ✨": "Voeg een glanzend accent toe ✨",
  "Add a shiny star on the shirt ⭐": "Voeg een glanzende ster op het shirt toe ⭐",
  "Add a side mirror and a puff of exhaust 💨": "Voeg een zijde spiegel en een wolkje of uitlaat 💨",
  "Add a smile and rosy cheeks 😊": "Voeg een glimlach en roze wangen toe 😊",
  "Add a smile and whiskers 〰️": "Voeg een glimlach en snorharen toe 〰️",
  "Add a smile 😄": "Voeg een glimlach toe 😄",
  "Add a smiley mouth and a forelock 😄": "Voeg een lachende mond en een voorpluk 😄",
  "Add a soft snout 🤎": "Voeg een zachte snuit toe 🤎",
  "Add a soft white tummy 🤍": "Voeg een zacht wit buikje toe 🤍",
  "Add a sparkly crown 👑": "Voeg een fonkelende kroon toe 👑",
  "Add a sparkly jewelled crown 👑": "Voeg een fonkelende kroon met juwelen toe 👑",
  "Add a splashy puddle 💧": "Voeg een spetterende plas toe 💧",
  "Add a splashy tail": "Voeg een spetterende staart toe",
  "Add a star in the top corner ⭐": "Voeg een ster in de bovenhoek toe ⭐",
  "Add a star with seven points ⭐": "Voeg een ster met zeven punten ⭐",
  "Add a star ⭐": "Voeg een ster ⭐",
  "Add a swishy tail": "Voeg een zwierige staart toe",
  "Add a thinner cross inside ➕": "Voeg een dunner kruis binnenin toe ➕",
  "Add a thinner stripe inside it": "Voeg een dunnere streep erin toe",
  "Add a third row of tiny spikes!": "Voeg een derde rij piepkleine stekeltjes toe!",
  "Add a triangle roof 🔺": "Voeg een driehoekig dak toe 🔺",
  "Add a wagging tail!": "Voeg een kwispelende staart toe!",
  "Add a water spout! 💦": "Voeg een waterstraal toe! 💦",
  "Add a white snout in the middle": "Voeg een witte snuit in de midden",
  "Add a wide orange beak 🟧": "Voeg een brede oranje snavel 🟧",
  "Add a wide stripe across the bottom": "Voeg een brede streep overdwars onderaan toe",
  "Add a zig-zag lightning bolt! ⚡": "Voeg een zig-zag bliksem bliksemschicht! ⚡",
  "Add a zig-zag line down ⚡": "Voeg een zigzaglijn naar beneden toe ⚡",
  "Add an apple on its back! 🍎": "Voeg een appel op zijn rug toe! 🍎",
  "Add an eagle on top 🦅": "Voeg een adelaar bovenop toe 🦅",
  "Add an eye and a happy smile 😊": "Voeg een oog en een blije glimlach toe 😊",
  "Add an eye and spots 😄": "Voeg een oog en stippen 😄",
  "Add an eye, a nostril and a smile 👀": "Voeg een oog, een neusgat en een glimlach toe 👀",
  "Add another row of rain": "Voeg nog een rij regen toe",
  "Add arms and legs": "Voeg armen en benen",
  "Add bark lines on the trunk": "Voeg schors lijnen op de stam",
  "Add big round wheels! ⚫": "Voeg grote ronde wielen! ⚫",
  "Add big sharp teeth! 🦷": "Voeg grote scherpe tanden! 🦷",
  "Add big spots on the wings": "Voeg grote stippen op de vleugels",
  "Add big swirls in the sky 🌀": "Voeg grote krullen in de lucht toe 🌀",
  "Add bumpy plates along its back!": "Voeg bobbelige platen toe langs zijn rug!",
  "Add colorful lips 👄": "Voeg kleurrijke lippen toe 👄",
  "Add cool sunglasses 😎": "Voeg een coole zonnebril toe 😎",
  "Add cool swooshy hair! 💇": "Voeg coole zwierige haren toe! 💇",
  "Add cosy stripes on its body 🐈": "Voeg gezellige strepen op zijn lichaam 🐈",
  "Add curly smoke from the chimney 💨": "Voeg krullende rook uit de schoorsteen toe 💨",
  "Add curvy scales 🐟": "Voeg gebogen schubben 🐟",
  "Add diagonal rays in the corners": "Voeg diagonale stralen in de hoeken toe",
  "Add eight petals all around! 🌸": "Voeg acht blaadjes rondom toe! 🌸",
  "Add even more rain! 🌧️": "Voeg nog meer regen toe! 🌧️",
  "Add eyes and a big smile 🎶": "Voeg ogen en een grote glimlach toe 🎶",
  "Add eyes and eyebrows 👀": "Teken ogen en wenkbrauwen 👀",
  "Add fins on top and bottom": "Voeg vinnen bovenop en onderaan toe",
  "Add five little stars ⭐": "Voeg vijf kleine sterren ⭐",
  "Add foamy claws on top ☁️": "Voeg schuimende klauwen bovenop toe ☁️",
  "Add four legs that join the body 🦵": "Voeg vier benen toe die aan het lichaam vastzitten 🦵",
  "Add four little feet 🦶": "Voeg vier kleine voetjes toe 🦶",
  "Add four little stars ✨": "Voeg vier kleine sterren ✨",
  "Add front legs and paws 🐾": "Voeg voorpoten en pootjes toe 🐾",
  "Add little Mount Fuji behind 🗻": "Voeg de kleine berg Fuji op de achtergrond toe 🗻",
  "Add little bars in the corners": "Voeg kleine staafjes in de hoeken toe",
  "Add little seeds in the middle": "Voeg kleine zaadjes in de midden",
  "Add little stars in the sky! ⭐": "Voeg kleine sterren in de lucht toe! ⭐",
  "Add more rays in the corners! 🌟": "Voeg meer stralen in de hoeken toe! 🌟",
  "Add nose, mouth and whiskers": "Voeg neus, mond en snorharen",
  "Add one big eye and a nostril 👀": "Voeg één groot oog en een neusgat toe 👀",
  "Add petals all around! 🌸": "Voeg blaadjes rondom toe! 🌸",
  "Add pretty spots! 🌈": "Voeg mooie stippen toe! 🌈",
  "Add raindrops falling from the clouds 💧": "Voeg regendruppels toe die uit de wolken vallen 💧",
  "Add rising bubbles! 🫧": "Voeg stijgende bellen toe! 🫧",
  "Add seeds and grass": "Voeg zaadjes en gras toe",
  "Add shiny hubcaps in the wheels": "Voeg glanzende wieldoppen in de wielen toe",
  "Add some big raindrops": "Voeg een paar grote regendruppels toe",
  "Add some yummy apples! 🍎": "Voeg wat heerlijke appels! 🍎",
  "Add sparkles all around! ✨": "Voeg fonkelingen rondom toe! ✨",
  "Add sparkles around it ✨": "Voeg fonkelingen eromheen toe ✨",
  "Add spikes on its back! ⚡": "Voeg stekels op zijn rug toe! ⚡",
  "Add straight sunny rays": "Voeg rechte zonnestralen toe",
  "Add swirly patterns on the wings 🌈": "Voeg krullerige patronen op de vleugels toe 🌈",
  "Add the back legs 🦵": "Voeg de achterpoten toe 🦵",
  "Add the big round shell 🐚": "Voeg de grote ronde schild 🐚",
  "Add the big top wings": "Voeg de grote bovenvleugels toe",
  "Add the body": "Voeg het lichaam",
  "Add the body and collar": "Voeg het lichaam en halsband",
  "Add the body and outfit": "Voeg het lichaam en het pak toe",
  "Add the body, arms and legs": "Voeg het lichaam, armen en benen",
  "Add the bottom wings": "Voeg de ondervleugels toe",
  "Add the collar at the neck 🧵": "Voeg de halsband om de nek toe 🧵",
  "Add the cuffs and the shoulder seams ✂️": "Voeg de manchetten en de schoudernaden toe ✂️",
  "Add the eye mask and eyes 👀": "Voeg het oogmasker en de ogen toe 👀",
  "Add the eye mask 🦸": "Voeg het oogmasker toe 🦸",
  "Add the eye, nose and a smile 👁️": "Voeg het oog, de neus en een glimlach toe 👁️",
  "Add the eyes and eyebrows 👀": "Voeg de ogen en wenkbrauwen toe 👀",
  "Add the eyes 👀": "Voeg de ogen 👀",
  "Add the family pet dog! 🐕": "Voeg de hond van het gezin toe! 🐕",
  "Add the first row of raindrops": "Voeg de eerste rij regendruppels toe",
  "Add the first row of spikes ⚡": "Voeg de eerste rij stekels toe ⚡",
  "Add the front legs and paws 🐾": "Voeg de voorpoten en pootjes toe 🐾",
  "Add the front legs — one prancing! 🦵": "Voeg de voorbenen — eentje steigerend! 🦵",
  "Add the gill line and an eye": "Voeg de kieuwlijn en een oog toe",
  "Add the hooves and a swishy tail!": "Voeg de hoeven en een zwierige staart toe!",
  "Add the microphone 🎤": "Voeg de microfoon 🎤",
  "Add the middle of the flower 🟡": "Voeg de midden of de bloem 🟡",
  "Add the nose, mouth and whiskers": "Voeg de neus, mond en snorharen",
  "Add the pointy cone underneath 🍦": "Voeg het puntige hoorntje eronder toe 🍦",
  "Add the road 🛣️": "Voeg de weg toe 🛣️",
  "Add the roof and a chimney 🔺": "Voeg het dak en een schoorsteen toe 🔺",
  "Add the round middle 🟡": "Voeg de ronde midden 🟡",
  "Add the sitting body": "Voeg het zittende lichaam toe",
  "Add the stars ⭐": "Voeg de sterren ⭐",
  "Add the underarm lines and the side seams 🧵": "Voeg de onderarmlijnen en de zijnaden toe 🧵",
  "Add the white parts of the eyes ⚪": "Voeg de witte delen van de ogen toe ⚪",
  "Add the windows 🪟": "Voeg de ramen toe 🪟",
  "Add thin red lines between the stripes": "Voeg dunne rode lijnen tussen de strepen",
  "Add twinkly stars! ⭐": "Voeg fonkelende sterren toe! ⭐",
  "Add two big eye bumps on top 👀": "Voeg twee grote oog bobbels bovenop 👀",
  "Add two big eyes 👀": "Voeg twee grote ogen 👀",
  "Add two big floppy ears 👂": "Voeg twee grote slappe oren 👂",
  "Add two big round ears 👂": "Voeg twee grote ronde oren 👂",
  "Add two big round eyes 👀": "Voeg twee grote ronde ogen 👀",
  "Add two big wheels ⚫": "Voeg twee grote wielen ⚫",
  "Add two ears on the sides 👂": "Voeg twee oren op de zijden 👂",
  "Add two eyes — make them different! 👁️": "Voeg twee ogen — maak ze verschillend! 👁️",
  "Add two eyes — one round, one from the side! 👁️": "Voeg twee ogen toe — één rond, één van de zijkant! 👁️",
  "Add two eyes 👀": "Voeg twee ogen 👀",
  "Add two floppy ears 👂": "Voeg twee slappe oren 👂",
  "Add two lines across": "Voeg twee lijnen overdwars",
  "Add two lines down": "Voeg twee lijnen naar beneden toe",
  "Add two little ears 👂": "Voeg twee kleine oren 👂",
  "Add two little feelers 👀": "Voeg twee kleine voelsprieten toe 👀",
  "Add two little feet 🧡": "Voeg twee kleine voetjes toe 🧡",
  "Add two little horns 🔶": "Voeg twee kleine hoorns 🔶",
  "Add two little tusks": "Voeg twee kleine slagtanden toe",
  "Add two pointy ears on top 🔺": "Voeg twee puntige oren bovenop 🔺",
  "Add two pointy ears 🔺": "Voeg twee puntige oren 🔺",
  "Add two puffy cheeks 😊": "Voeg twee bolle wangen toe 😊",
  "Add two rosy cheeks 😊": "Voeg twee roze wangen 😊",
  "Add two round ears on top 👂": "Voeg twee ronde oren bovenop 👂",
  "Add two round eyes 👀": "Voeg twee ronde ogen 👀",
  "Add two rows of curvy scales 🐟": "Voeg twee rijen gebogen schubben toe 🐟",
  "Add two tall ears 👂": "Voeg twee hoge oren 👂",
  "Add two tiny nostrils 👃": "Voeg twee piepkleine neusgaatjes toe 👃",
  "Add two windows 🪟": "Voeg twee ramen toe 🪟",
  "Add wavy seaweed 🌿": "Voeg golvend zeewier toe 🌿",
  "Add windows with little panes 🪟": "Voeg ramen met kleine ruitjes toe 🪟",
  "Add yummy apples! 🍎": "Voeg heerlijke appels toe! 🍎",
  "Almost there 🔵": "Bijna klaar 🔵",
  "And one on the right! ☁️": "en eentje rechts! ☁️",
  "And that’s little you! 🙂": "en dat ben jij, klein! 🙂",
  "Another arc just inside 🟠": "nog een boog net erbinnen 🟠",
  "Another one 🟢": "Nog een 🟢",
  "Brew a storm cloud on the right! ☁️": "Tover een stormwolk rechts! ☁️",
  "Buttery yellow for the horn! 🌟": "Boterig geel voor de hoorn! 🌟",
  "Colour the red trim first! 🖍️": "Kleur eerst de rode bies! 🖍️",
  "Draw 4 thin red diagonal stripes ╳": "Teken 4 dunne rode diagonale strepen ╳",
  "Draw 8 blue triangles in the corners 🔺": "Teken 8 blauwe driehoeken in de hoeken 🔺",
  "Draw Daddy 🧔": "Teken papa 🧔",
  "Draw Mommy 👩": "Teken mama 👩",
  "Draw a big circle in the middle ⭕": "Teken een grote cirkel in de midden ⭕",
  "Draw a big diamond 💎": "Teken een grote ruit 💎",
  "Draw a big fluffy cloud ☁️": "Teken een grote donzige wolk ☁️",
  "Draw a big fluffy mane! 🦁": "Teken een grote donzige manen! 🦁",
  "Draw a big head shape 🟣": "Teken een grote hoofd vorm 🟣",
  "Draw a big red cross ✚": "Teken een grote rode kruis ✚",
  "Draw a big round body 🦕": "Teken een grote ronde lichaam 🦕",
  "Draw a big round body! 🐳": "Teken een grote ronde lichaam! 🐳",
  "Draw a big round head! 🐹": "Teken een grote ronde hoofd! 🐹",
  "Draw a big round head! 🐻": "Teken een grote ronde hoofd! 🐻",
  "Draw a big round penguin body! 🐧": "Teken een grote ronde pinguïn lichaam! 🐧",
  "Draw a big round sun ☀️": "Teken een grote ronde zon ☀️",
  "Draw a big star in the middle ⭐": "Teken een grote ster in de midden ⭐",
  "Draw a big thick red cross ✚": "Teken een grote dikke rode kruis ✚",
  "Draw a big twinkly star ⭐": "Teken een grote fonkelende ster ⭐",
  "Draw a big white X corner to corner ╳": "Teken een grote witte X van hoek tot hoek ╳",
  "Draw a big yellow triangle 🔺": "Teken een grote gele driehoek 🔺",
  "Draw a circle in the middle ⭕": "Teken een cirkel in de midden ⭕",
  "Draw a circle inside ⭕": "Teken een cirkel binnenin ⭕",
  "Draw a crescent moon 🌙": "Teken een halve maan 🌙",
  "Draw a curvy line down the middle — that splits the face!": "Teken een gebogen lijn in het midden — die verdeelt het gezicht!",
  "Draw a five-point star in the middle ⭐": "Teken een vijfpuntige ster in het midden ⭐",
  "Draw a fluffy cloud ☁️": "Teken een donzige wolk ☁️",
  "Draw a green five-point star ⭐": "Teken een groene vijfpuntige ster ⭐",
  "Draw a line on the beak 〰️": "Teken een lijn op de snavel 〰️",
  "Draw a little cactus in the middle 🌵": "Teken een kleine cactus in de midden 🌵",
  "Draw a little eagle in the middle 🦅": "Teken een kleine adelaar in het midden 🦅",
  "Draw a little house behind 🏠": "Teken een klein huisje op de achtergrond 🏠",
  "Draw a little red emblem in the middle 🌷": "Teken een klein rood embleem in het midden 🌷",
  "Draw a little white box in the middle": "Teken een kleine witte vak in de midden",
  "Draw a long horse head and neck from the side 🟫": "Teken een lange paard hoofd en nek van de zijde 🟫",
  "Draw a long sword underneath 🗡️": "Teken een lang zwaard eronder 🗡️",
  "Draw a maple leaf in the middle 🍁": "Teken een esdoornblad in het midden 🍁",
  "Draw a palm tree on a hill 🌴": "Teken een palmboom op een heuvel 🌴",
  "Draw a round badge in the middle": "Teken een rond insigne in het midden",
  "Draw a round head 🟠": "Teken een ronde hoofd 🟠",
  "Draw a round head! 🐘": "Teken een ronde hoofd! 🐘",
  "Draw a round head! 🐭": "Teken een ronde hoofd! 🐭",
  "Draw a round head! 🐮": "Teken een ronde hoofd! 🐮",
  "Draw a round head! 🐰": "Teken een ronde hoofd! 🐰",
  "Draw a round head! 🐷": "Teken een ronde hoofd! 🐷",
  "Draw a round head! 🦆": "Teken een ronde hoofd! 🦆",
  "Draw a round head! 🦊": "Teken een ronde hoofd! 🦊",
  "Draw a shield and a crown 👑": "Teken een schild en een kroon 👑",
  "Draw a shield in the middle 🛡️": "Teken een schild in de midden 🛡️",
  "Draw a shield with a bird on top 🛡️": "Teken een schild met een vogel bovenop 🛡️",
  "Draw a sideways green Y across the flag": "Teken een schuine groene Y overdwars op de vlag",
  "Draw a smiling sun in the box ☀️": "Teken een lachende zon in het vak ☀️",
  "Draw a smiling sun in the middle ☀️": "Teken een lachende zon in het midden ☀️",
  "Draw a star inside a ring ⭐": "Teken een ster binnenin een ring ⭐",
  "Draw a triangle at the left edge 🔺": "Teken een driehoek aan de linkerrand 🔺",
  "Draw a triangle from the side 🔺": "Teken een driehoek van de zijde 🔺",
  "Draw a triangle on the left 🔺": "Teken een driehoek aan de linkerkant 🔺",
  "Draw a twinkly star ⭐": "Teken een fonkelende ster ⭐",
  "Draw a wheel in the middle ☸️": "Teken een wiel in de midden ☸️",
  "Draw a wide round head! 🐸": "Teken een brede ronde hoofd! 🐸",
  "Draw a wide stripe corner to corner": "Teken een brede streep van hoek tot hoek",
  "Draw a wonky face shape 🟦": "Teken een scheef gezicht 🟦",
  "Draw her big flowy dress 👗": "Teken haar grote wapperende jurk 👗",
  "Draw her castle in the distance 🏰": "Teken haar kasteel in de verte 🏰",
  "Draw her hair fringe": "Teken haar pony",
  "Draw one big star ⭐": "Teken een grote ster ⭐",
  "Draw the back and the round tummy 🐴": "Teken de rug en het ronde buikje 🐴",
  "Draw the big curling wave 🌊": "Teken de grote krullende golf 🌊",
  "Draw the big happy eyes 👀": "Teken de grote blije ogen 👀",
  "Draw the biggest arc 🔴": "Teken de grootste boog 🔴",
  "Draw the body and antennae 🦋": "Teken het lichaam en de antennes 🦋",
  "Draw the body, segments and antennae": "Teken het lichaam, de segmenten en de antennes",
  "Draw the car body 🚗": "Teken de auto-carrosserie 🚗",
  "Draw the door with a doorknob 🚪": "Teken de deur met een deurknop 🚪",
  "Draw the door 🚪": "Teken de deur 🚪",
  "Draw the face and cool hair": "Teken het gezicht en stoer haar",
  "Draw the fish body": "Teken de vis lichaam",
  "Draw the fish body 🐟": "Teken de vis lichaam 🐟",
  "Draw the flag's box 🏁": "Teken het vak van de vlag 🏁",
  "Draw the flowing mane": "Teken de wapperende manen",
  "Draw the flowing mane down the neck 💇": "Teken de wapperende manen langs de nek 💇",
  "Draw the grassy ground 🌱": "Teken de grasgrond 🌱",
  "Draw the happy eyes 👀": "Teken de blije ogen 👀",
  "Draw the head with a soft snout 🐴": "Teken het hoofd met een zachte snuit 🐴",
  "Draw the hem, then start the big number 🔢": "Teken de zoom, begin dan aan het grote cijfer 🔢",
  "Draw the hero's head 🦸": "Teken het hoofd van de held 🦸",
  "Draw the left side of the heart": "Teken de linkerzijde van het hart",
  "Draw the magic horn 🌟": "Teken de magische hoorn 🌟",
  "Draw the nose and a smiley mouth 👃": "Teken de neus en een lachende mond 👃",
  "Draw the nose and mouth 👃": "Teken de neus en mond 👃",
  "Draw the rolling hills at the bottom 🏞️": "Teken de glooiende heuvels onderaan 🏞️",
  "Draw the round body and pointy nose 🟤": "Teken de ronde lichaam en puntige neus 🟤",
  "Draw the round ice cream scoop 🍦": "Teken de ronde schep ijs 🍦",
  "Draw the shoulders and the sleeves 💪": "Teken de schouders en de mouwen 💪",
  "Draw the snail's long foot 🐌": "Teken de lange voet van de slak 🐌",
  "Draw the stem and a leaf 🌿": "Teken de stengel en een blad 🌿",
  "Draw the stem and two leaves 🌿": "Teken de stengel en twee bladeren 🌿",
  "Draw the swirl on the shell 🌀": "Teken de krul op het schild 🌀",
  "Draw the trunk with roots 🟫": "Teken de stam met wortels 🟫",
  "Draw the trunk 🟫": "Teken de stam 🟫",
  "Draw the two sides and the bottom of the shirt 👕": "Teken de twee zijkanten en de onderkant van het shirt 👕",
  "Draw the waffle lines on the cone 🧇": "Teken de wafellijnen op het hoorntje 🧇",
  "Draw the walls (a big square) 🟫": "Teken de muren (een groot vierkant) 🟫",
  "Draw two stars ⭐": "Teken twee sterren ⭐",
  "Draw two white stars in the corner ⭐": "Teken twee witte sterren in de hoek ⭐",
  "Draw your big brother or sister 🧒": "Teken je grote broer of zus 🧒",
  "Fill it with a checkerboard ▩": "Vul het met een dambordpatroon ▩",
  "Finish the arcs with violet! 🟣": "Maak de bogen af met violet! 🟣",
  "Finish with a ball of yarn to play with! 🧶": "Eindig met een bol wol om mee te spelen! 🧶",
  "Finish with a bright sun! ☀️": "Eindig met een felle zon! ☀️",
  "Finish with a curly tail!": "Eindig met een krullende staart!",
  "Finish with a flower and grass 🌸": "Eindig met een bloem en gras 🌸",
  "Finish with a fluffy cloud ☁️": "Eindig met een donzige wolk ☁️",
  "Finish with a friendly eye! 😄": "Eindig met een vriendelijke oog! 😄",
  "Finish with a happy smile! 😄": "Eindig met een blije glimlach! 😄",
  "Finish with a happy smile! 🦔": "Eindig met een blije glimlach! 🦔",
  "Finish with a lightning emblem in a shield! ⚡": "Eindig met een bliksemembleem in een schild! ⚡",
  "Finish with a lightning emblem on the chest! ⚡": "Eindig met een bliksemembleem op de borst! ⚡",
  "Finish with a row of flowers below! 🌷": "Eindig met een rij of bloemen onder! 🌷",
  "Finish with a shining sun! ☀️": "Eindig met een stralende zon! ☀️",
  "Finish with a sparkly star! ⭐": "Eindig met een fonkelende ster! ⭐",
  "Finish with a splashy puddle 💧": "Eindig met een spetterende plas 💧",
  "Finish with a sun and a path! ☀️": "Eindig met een zon en een pad! ☀️",
  "Finish with a sun and birds! ☀️🐦": "Eindig met een zon en vogels! ☀️🐦",
  "Finish with a sunny sky! ☀️": "Eindig met een zonnige lucht! ☀️",
  "Finish with a wagging tail!": "Eindig met een kwispelende staart!",
  "Finish with an apple on its back! 🍎": "Eindig met een appel op zijn rug! 🍎",
  "Finish with an eye and a smile! 🐠": "Eindig met een oog en een glimlach! 🐠",
  "Finish with an eye and spots 😄": "Eindig met een oog en stippen 😄",
  "Finish with birds flying by! 🐦": "Eindig met vogels die voorbij vliegen! 🐦",
  "Finish with bubbles! 🫧": "Eindig met bellen! 🫧",
  "Finish with cubist lines! 🎨": "Eindig met kubistische lijnen! 🎨",
  "Finish with grass at the bottom! 🌱": "Eindig met gras onderaan! 🌱",
  "Finish with little boats riding the sea ⛵": "Eindig met kleine bootjes die op de zee varen ⛵",
  "Finish with little hearts floating up!": "Eindig met kleine zwevende hartjes!",
  "Finish with little hooves and a swishy tail!": "Eindig met kleine hoeven en een zwierige staart!",
  "Finish with musical notes and a star! 🎵⭐": "Eindig met muzieknoten en een ster! 🎵⭐",
  "Finish with musical notes! 🎵": "Eindig met muzieknoten! 🎵",
  "Finish with swirly wing patterns! 🌈": "Eindig met krullerige vleugelpatronen! 🌈",
  "Finish with the ground and a bone! 🦴": "Eindig met de grond en een bot! 🦴",
  "Finish with the ground and a fern 🌿": "Eindig met de grond en een varen 🌿",
  "Finish with the road! 🛣️": "Eindig met de weg! 🛣️",
  "Finish with the sun! ☀️": "Eindig met de zon! ☀️",
  "Finish with the tall cypress tree 🌲": "Eindig met de hoge cipres 🌲",
  "Finish with two little hearts!": "Eindig met twee kleine hartjes!",
  "Finish with whiskers and a smile! 😺": "Eindig met snorharen en een glimlach! 😺",
  "Finish with wild hair and an ear! 🎨": "Eindig met wild haar en een oor! 🎨",
  "Flow the wavy mane down the neck 🌊": "Laat de golvende manen langs de nek vloeien 🌊",
  "Give her a happy smile 😊": "Geef haar een blije glimlach 😊",
  "Give it a cute nose 👃": "Geef het een schattig neusje 👃",
  "Give it a happy face! 😊": "Geef het een blij gezicht! 😊",
  "Give it an eye and a nose 👁️": "Geef het an oog en een neus 👁️",
  "Give it four chunky legs and feet": "Geef het vier dikke poten en voetjes",
  "Give the number its 3-D edge 🔢": "Geef het cijfer een 3-D rand 🔢",
  "Give the pony a saddle 🏇": "Geef de pony een zadel 🏇",
  "Give the sun a happy face 😊": "Geef de zon een blij gezichtje 😊",
  "Give the sun a happy face 🙂": "Geef de zon een blij gezichtje 🙂",
  "Green next 🟢": "dan groen 🟢",
  "Hang a little swing from a branch 🪢": "Hang een kleine schommel aan een tak 🪢",
  "Indigo after that": "Daarna indigo",
  "Keep going 🟡": "Ga door 🟡",
  "Last of all, write the dog's name: D-O-G! ✏️": "Als laatste, schrijf de naam van de hond: H-O-N-D! ✏️",
  "Last of all, write the name: A-L-G-E-R-I-A! ✏️": "Als laatste, schrijf de naam: A-L-G-E-R-I-A! ✏️",
  "Last of all, write the name: A-R-G-E-N-T-I-N-A! ✏️": "Als laatste, schrijf de naam: A-R-G-E-N-T-I-N-A! ✏️",
  "Last of all, write the name: A-U-S-T-R-A-L-I-A! ✏️": "Als laatste, schrijf de naam: A-U-S-T-R-A-L-I-A! ✏️",
  "Last of all, write the name: A-U-S-T-R-I-A! ✏️": "Als laatste, schrijf de naam: A-U-S-T-R-I-A! ✏️",
  "Last of all, write the name: B-E-L-G-I-U-M! ✏️": "Als laatste, schrijf de naam: B-E-L-G-I-U-M! ✏️",
  "Last of all, write the name: B-O-S-N-I-A-·-A-N-D-·-H-E-R-Z-E-G-O-V-I-N-A! ✏️": "Als laatste, schrijf de naam: B-O-S-N-I-A-·-A-N-D-·-H-E-R-Z-E-G-O-V-I-N-A! ✏️",
  "Last of all, write the name: B-R-A-Z-I-L! ✏️": "Als laatste, schrijf de naam: B-R-A-Z-I-L! ✏️",
  "Last of all, write the name: B-U-T-T-E-R-F-L-Y! ✏️": "Als laatste, schrijf de naam: B-U-T-T-E-R-F-L-Y! ✏️",
  "Last of all, write the name: C-A-N-A-D-A! ✏️": "Als laatste, schrijf de naam: C-A-N-A-D-A! ✏️",
  "Last of all, write the name: C-A-P-E-·-V-E-R-D-E! ✏️": "Als laatste, schrijf de naam: C-A-P-E-·-V-E-R-D-E! ✏️",
  "Last of all, write the name: C-A-R! ✏️": "Als laatste, schrijf de naam: C-A-R! ✏️",
  "Last of all, write the name: C-A-T! ✏️": "Als laatste, schrijf de naam: C-A-T! ✏️",
  "Last of all, write the name: C-H-I-N-A! ✏️": "Als laatste, schrijf de naam: C-H-I-N-A! ✏️",
  "Last of all, write the name: C-I-R-C-L-E! ✏️": "Als laatste, schrijf de naam: C-I-R-C-L-E! ✏️",
  "Last of all, write the name: C-L-O-U-D! ✏️": "Als laatste, schrijf de naam: C-L-O-U-D! ✏️",
  "Last of all, write the name: C-O-L-O-M-B-I-A! ✏️": "Als laatste, schrijf de naam: C-O-L-O-M-B-I-A! ✏️",
  "Last of all, write the name: C-R-E-S-C-E-N-T! ✏️": "Als laatste, schrijf de naam: C-R-E-S-C-E-N-T! ✏️",
  "Last of all, write the name: C-R-O-A-T-I-A! ✏️": "Als laatste, schrijf de naam: C-R-O-A-T-I-A! ✏️",
  "Last of all, write the name: C-U-R-A-C-A-O! ✏️": "Als laatste, schrijf de naam: C-U-R-A-C-A-O! ✏️",
  "Last of all, write the name: C-Z-E-C-H-·-R-E-P-U-B-L-I-C! ✏️": "Als laatste, schrijf de naam: C-Z-E-C-H-·-R-E-P-U-B-L-I-C! ✏️",
  "Last of all, write the name: D-E-N-M-A-R-K! ✏️": "Als laatste, schrijf de naam: D-E-N-M-A-R-K! ✏️",
  "Last of all, write the name: D-I-A-M-O-N-D! ✏️": "Als laatste, schrijf de naam: D-I-A-M-O-N-D! ✏️",
  "Last of all, write the name: D-I-N-O-S-A-U-R! ✏️": "Als laatste, schrijf de naam: D-I-N-O-S-A-U-R! ✏️",
  "Last of all, write the name: D-R-·-C-O-N-G-O! ✏️": "Als laatste, schrijf de naam: D-R-·-C-O-N-G-O! ✏️",
  "Last of all, write the name: E-C-U-A-D-O-R! ✏️": "Als laatste, schrijf de naam: E-C-U-A-D-O-R! ✏️",
  "Last of all, write the name: E-G-Y-P-T! ✏️": "Als laatste, schrijf de naam: E-G-Y-P-T! ✏️",
  "Last of all, write the name: E-N-G-L-A-N-D! ✏️": "Als laatste, schrijf de naam: E-N-G-L-A-N-D! ✏️",
  "Last of all, write the name: F-A-M-I-L-Y! ✏️": "Als laatste, schrijf de naam: F-A-M-I-L-Y! ✏️",
  "Last of all, write the name: F-I-N-L-A-N-D! ✏️": "Als laatste, schrijf de naam: F-I-N-L-A-N-D! ✏️",
  "Last of all, write the name: F-I-S-H! ✏️": "Als laatste, schrijf de naam: F-I-S-H! ✏️",
  "Last of all, write the name: F-L-O-W-E-R! ✏️": "Als laatste, schrijf de naam: F-L-O-W-E-R! ✏️",
  "Last of all, write the name: F-R-A-N-C-E! ✏️": "Als laatste, schrijf de naam: F-R-A-N-C-E! ✏️",
  "Last of all, write the name: G-E-R-M-A-N-Y! ✏️": "Als laatste, schrijf de naam: G-E-R-M-A-N-Y! ✏️",
  "Last of all, write the name: G-H-A-N-A! ✏️": "Als laatste, schrijf de naam: G-H-A-N-A! ✏️",
  "Last of all, write the name: G-R-E-E-C-E! ✏️": "Als laatste, schrijf de naam: G-R-E-E-C-E! ✏️",
  "Last of all, write the name: H-A-I-T-I! ✏️": "Als laatste, schrijf de naam: H-A-I-T-I! ✏️",
  "Last of all, write the name: H-E-A-R-T! ✏️": "Als laatste, schrijf de naam: H-E-A-R-T! ✏️",
  "Last of all, write the name: H-E-D-G-E-H-O-G! ✏️": "Als laatste, schrijf de naam: H-E-D-G-E-H-O-G! ✏️",
  "Last of all, write the name: H-E-X-A-G-O-N! ✏️": "Als laatste, schrijf de naam: H-E-X-A-G-O-N! ✏️",
  "Last of all, write the name: H-O-R-S-E! ✏️": "Als laatste, schrijf de naam: H-O-R-S-E! ✏️",
  "Last of all, write the name: H-O-U-S-E! ✏️": "Als laatste, schrijf de naam: H-O-U-S-E! ✏️",
  "Last of all, write the name: I-C-E-·-C-R-E-A-M! ✏️": "Als laatste, schrijf de naam: I-C-E-·-C-R-E-A-M! ✏️",
  "Last of all, write the name: I-N-D-I-A! ✏️": "Als laatste, schrijf de naam: I-N-D-I-A! ✏️",
  "Last of all, write the name: I-R-A-N! ✏️": "Als laatste, schrijf de naam: I-R-A-N! ✏️",
  "Last of all, write the name: I-R-A-Q! ✏️": "Als laatste, schrijf de naam: I-R-A-Q! ✏️",
  "Last of all, write the name: I-R-E-L-A-N-D! ✏️": "Als laatste, schrijf de naam: I-R-E-L-A-N-D! ✏️",
  "Last of all, write the name: I-T-A-L-Y! ✏️": "Als laatste, schrijf de naam: I-T-A-L-Y! ✏️",
  "Last of all, write the name: I-V-O-R-Y-·-C-O-A-S-T! ✏️": "Als laatste, schrijf de naam: I-V-O-R-Y-·-C-O-A-S-T! ✏️",
  "Last of all, write the name: J-A-P-A-N! ✏️": "Als laatste, schrijf de naam: J-A-P-A-N! ✏️",
  "Last of all, write the name: J-O-R-D-A-N! ✏️": "Als laatste, schrijf de naam: J-O-R-D-A-N! ✏️",
  "Last of all, write the name: M-E-X-I-C-O! ✏️": "Als laatste, schrijf de naam: M-E-X-I-C-O! ✏️",
  "Last of all, write the name: M-O-R-O-C-C-O! ✏️": "Als laatste, schrijf de naam: M-O-R-O-C-C-O! ✏️",
  "Last of all, write the name: N-E-T-H-E-R-L-A-N-D-S! ✏️": "Als laatste, schrijf de naam: N-E-T-H-E-R-L-A-N-D-S! ✏️",
  "Last of all, write the name: N-E-W-·-Z-E-A-L-A-N-D! ✏️": "Als laatste, schrijf de naam: N-E-W-·-Z-E-A-L-A-N-D! ✏️",
  "Last of all, write the name: N-O-R-W-A-Y! ✏️": "Als laatste, schrijf de naam: N-O-R-W-A-Y! ✏️",
  "Last of all, write the name: O-C-T-A-G-O-N! ✏️": "Als laatste, schrijf de naam: O-C-T-A-G-O-N! ✏️",
  "Last of all, write the name: O-V-A-L! ✏️": "Als laatste, schrijf de naam: O-V-A-L! ✏️",
  "Last of all, write the name: P-A-N-A-M-A! ✏️": "Als laatste, schrijf de naam: P-A-N-A-M-A! ✏️",
  "Last of all, write the name: P-A-R-A-G-U-A-Y! ✏️": "Als laatste, schrijf de naam: P-A-R-A-G-U-A-Y! ✏️",
  "Last of all, write the name: P-A-R-A-L-L-E-L-O-G-R-A-M! ✏️": "Als laatste, schrijf de naam: P-A-R-A-L-L-E-L-O-G-R-A-M! ✏️",
  "Last of all, write the name: P-E-N-T-A-G-O-N! ✏️": "Als laatste, schrijf de naam: P-E-N-T-A-G-O-N! ✏️",
  "Last of all, write the name: P-O-P-·-S-T-A-R! ✏️": "Als laatste, schrijf de naam: P-O-P-·-S-T-A-R! ✏️",
  "Last of all, write the name: P-O-R-T-U-G-A-L! ✏️": "Als laatste, schrijf de naam: P-O-R-T-U-G-A-L! ✏️",
  "Last of all, write the name: P-R-I-N-C-E-S-S! ✏️": "Als laatste, schrijf de naam: P-R-I-N-C-E-S-S! ✏️",
  "Last of all, write the name: Q-A-T-A-R! ✏️": "Als laatste, schrijf de naam: Q-A-T-A-R! ✏️",
  "Last of all, write the name: R-A-I-N-B-O-W! ✏️": "Als laatste, schrijf de naam: R-A-I-N-B-O-W! ✏️",
  "Last of all, write the name: R-E-C-T-A-N-G-L-E! ✏️": "Als laatste, schrijf de naam: R-E-C-T-A-N-G-L-E! ✏️",
  "Last of all, write the name: S-A-U-D-I-·-A-R-A-B-I-A! ✏️": "Als laatste, schrijf de naam: S-A-U-D-I-·-A-R-A-B-I-A! ✏️",
  "Last of all, write the name: S-C-O-T-L-A-N-D! ✏️": "Als laatste, schrijf de naam: S-C-O-T-L-A-N-D! ✏️",
  "Last of all, write the name: S-E-N-E-G-A-L! ✏️": "Als laatste, schrijf de naam: S-E-N-E-G-A-L! ✏️",
  "Last of all, write the name: S-I-N-G-A-P-O-R-E! ✏️": "Als laatste, schrijf de naam: S-I-N-G-A-P-O-R-E! ✏️",
  "Last of all, write the name: S-N-A-I-L! ✏️": "Als laatste, schrijf de naam: S-N-A-I-L! ✏️",
  "Last of all, write the name: S-O-U-T-H-·-A-F-R-I-C-A! ✏️": "Als laatste, schrijf de naam: S-O-U-T-H-·-A-F-R-I-C-A! ✏️",
  "Last of all, write the name: S-O-U-T-H-·-K-O-R-E-A! ✏️": "Als laatste, schrijf de naam: S-O-U-T-H-·-K-O-R-E-A! ✏️",
  "Last of all, write the name: S-P-A-I-N! ✏️": "Als laatste, schrijf de naam: S-P-A-I-N! ✏️",
  "Last of all, write the name: S-Q-U-A-R-E! ✏️": "Als laatste, schrijf de naam: S-Q-U-A-R-E! ✏️",
  "Last of all, write the name: S-T-A-R! ✏️": "Als laatste, schrijf de naam: S-T-A-R! ✏️",
  "Last of all, write the name: S-U-N! ✏️": "Als laatste, schrijf de naam: S-U-N! ✏️",
  "Last of all, write the name: S-U-P-E-R-·-S-T-O-R-M-Y! ✏️": "Als laatste, schrijf de naam: S-U-P-E-R-·-S-T-O-R-M-Y! ✏️",
  "Last of all, write the name: S-W-E-D-E-N! ✏️": "Als laatste, schrijf de naam: S-W-E-D-E-N! ✏️",
  "Last of all, write the name: S-W-I-T-Z-E-R-L-A-N-D! ✏️": "Als laatste, schrijf de naam: S-W-I-T-Z-E-R-L-A-N-D! ✏️",
  "Last of all, write the name: T-R-A-P-E-Z-I-U-M! ✏️": "Als laatste, schrijf de naam: T-R-A-P-E-Z-I-U-M! ✏️",
  "Last of all, write the name: T-R-E-E! ✏️": "Als laatste, schrijf de naam: T-R-E-E! ✏️",
  "Last of all, write the name: T-R-I-A-N-G-L-E! ✏️": "Als laatste, schrijf de naam: T-R-I-A-N-G-L-E! ✏️",
  "Last of all, write the name: T-U-N-I-S-I-A! ✏️": "Als laatste, schrijf de naam: T-U-N-I-S-I-A! ✏️",
  "Last of all, write the name: T-U-R-K-E-Y! ✏️": "Als laatste, schrijf de naam: T-U-R-K-E-Y! ✏️",
  "Last of all, write the name: U-N-I-C-O-R-N! ✏️": "Als laatste, schrijf de naam: U-N-I-C-O-R-N! ✏️",
  "Last of all, write the name: U-N-I-T-E-D-·-A-R-A-B-·-E-M-I-R-A-T-E-S! ✏️": "Als laatste, schrijf de naam: U-N-I-T-E-D-·-A-R-A-B-·-E-M-I-R-A-T-E-S! ✏️",
  "Last of all, write the name: U-N-I-T-E-D-·-K-I-N-G-D-O-M! ✏️": "Als laatste, schrijf de naam: U-N-I-T-E-D-·-K-I-N-G-D-O-M! ✏️",
  "Last of all, write the name: U-N-I-T-E-D-·-S-T-A-T-E-S! ✏️": "Als laatste, schrijf de naam: U-N-I-T-E-D-·-S-T-A-T-E-S! ✏️",
  "Last of all, write the name: U-R-U-G-U-A-Y! ✏️": "Als laatste, schrijf de naam: U-R-U-G-U-A-Y! ✏️",
  "Last of all, write the name: U-Z-B-E-K-I-S-T-A-N! ✏️": "Als laatste, schrijf de naam: U-Z-B-E-K-I-S-T-A-N! ✏️",
  "Lilac swirls next! 💜": "Nu lila krullen! 💜",
  "Make a little flag box in the corner": "Maak een kleine vlag vak in de hoek",
  "Make a sunny meadow with grass ☀️": "Maak een zonnig weiland met gras ☀️",
  "Now bring it to life with color! 🎨": "Geef het nu kleur! 🎨",
  "Now color her all in! 🖍️": "Kleur haar nu helemaal in! 🖍️",
  "Now color him all in! 🖍️": "Kleur hem nu helemaal in! 🖍️",
  "Now color it all in! 🖍️": "Kleur het nu helemaal in! 🖍️",
  "Now color it in! 🖍️": "Kleur het nu in! 🖍️",
  "Now color the bear in! 🖍️": "Kleur nu de beer in! 🖍️",
  "Now color the bunny in! 🖍️": "Kleur nu het konijn in! 🖍️",
  "Now color the cloud in! 🖍️": "Kleur nu de wolk in! 🖍️",
  "Now color the cow in! 🖍️": "Kleur nu de koe in! 🖍️",
  "Now color the duck in! 🖍️": "Kleur nu de eend in! 🖍️",
  "Now color the elephant in! 🖍️": "Kleur nu de olifant in! 🖍️",
  "Now color the fish in! 🖍️": "Kleur nu de vis in! 🖍️",
  "Now color the fox in! 🖍️": "Kleur nu de vos in! 🖍️",
  "Now color the frog in! 🖍️": "Kleur nu de kikker in! 🖍️",
  "Now color the hamster in! 🖍️": "Kleur nu de hamster in! 🖍️",
  "Now color the horse in! 🖍️": "Kleur nu het paard in! 🖍️",
  "Now color the lion in! 🖍️": "Kleur nu de leeuw in! 🖍️",
  "Now color the mouse in! 🖍️": "Kleur nu de muis in! 🖍️",
  "Now color the penguin in! 🖍️": "Kleur nu de pinguïn in! 🖍️",
  "Now color the pig in! 🖍️": "Kleur nu het varken in! 🖍️",
  "Now color the whale in! 🖍️": "Kleur nu de walvis in! 🖍️",
  "Now color the whole rainbow in! 🌈": "Kleur nu de hele regenboog in! 🌈",
  "Now color the whole sky in! 🌈": "Kleur nu de hele lucht in! 🌈",
  "Now colour the flag in! 🖍️": "Kleur nu de vlag in! 🖍️",
  "Now colour the shirt blue! 🖍️": "Kleur het shirt nu blauw! 🖍️",
  "Now colour your circle in! 🖍️": "Kleur nu je cirkel in! 🖍️",
  "Now colour your crescent in! 🖍️": "Kleur nu je halve maan in! 🖍️",
  "Now colour your diamond in! 🖍️": "Kleur nu je ruit in! 🖍️",
  "Now colour your family in! 🖍️": "Kleur nu je gezin in! 🖍️",
  "Now colour your family scene in! 🖍️": "Kleur nu je gezinscène in! 🖍️",
  "Now colour your heart in! 🖍️": "Kleur nu je hart in! 🖍️",
  "Now colour your hexagon in! 🖍️": "Kleur nu je zeshoek in! 🖍️",
  "Now colour your ice cream in! 🖍️": "Kleur nu je ijsje in! 🖍️",
  "Now colour your octagon in! 🖍️": "Kleur nu je achthoek in! 🖍️",
  "Now colour your oval in! 🖍️": "Kleur nu je ovaal in! 🖍️",
  "Now colour your parallelogram in! 🖍️": "Kleur nu je parallellogram in! 🖍️",
  "Now colour your pentagon in! 🖍️": "Kleur nu je vijfhoek in! 🖍️",
  "Now colour your rectangle in! 🖍️": "Kleur nu je rechthoek in! 🖍️",
  "Now colour your snail in! 🖍️": "Kleur nu je slak in! 🖍️",
  "Now colour your square in! 🖍️": "Kleur nu je vierkant in! 🖍️",
  "Now colour your star in! 🖍️": "Kleur nu je ster in! 🖍️",
  "Now colour your trapezium in! 🖍️": "Kleur nu je trapezium in! 🖍️",
  "Now colour your triangle in! 🖍️": "Kleur nu je driehoek in! 🖍️",
  "Now colour your unicorn in! 🖍️": "Kleur nu je eenhoorn in! 🖍️",
  "Now creamy peach swirls! 🍑": "Nu romige perzikkrullen! 🍑",
  "Now draw two big eyes 👀": "Teken nu twee grote ogen 👀",
  "Now mirror it on the right! ❤️": "Spiegel het nu aan de rechterkant! ❤️",
  "Now orange 🟠": "Nu oranje 🟠",
  "Now two sparkly eyes 👀": "Nu twee fonkelende ogen 👀",
  "Pink for the mane! 🩷": "roze voor de manen! 🩷",
  "Plant a tree beside the house 🌳": "Plant een boom naast het huis 🌳",
  "Put the star on a stage with a spotlight! 🔦": "Zet de ster op een podium met een spotlight! 🔦",
  "Shoot an arrow through it! 🏹": "Schiet een pijl door het! 🏹",
  "Split it into four boxes": "Verdeel het in vier vakken",
  "Spread a magical wing ✨": "Spreid een magische vleugel ✨",
  "Start with a big round head 🟤": "Begin met een grote ronde hoofd 🟤",
  "Start with a big round head! 🟤": "Begin met een grote ronde hoofd! 🟤",
  "Start with a big rounded body": "Begin met een groot afgerond lichaam",
  "Start with a red arc 🔴": "Begin met een rode boog 🔴",
  "Start with the pony's body": "Begin met de pony's lichaam",
  "Start with the round head": "Begin met de ronde hoofd",
  "Sweep the fringe around the horn 💇": "Veeg de pony rond de hoorn 💇",
  "Swish a big wavy tail 🌊": "Zwaai een grote golvende staart 🌊",
  "The littlest arc 🟣": "De kleinste boog 🟣",
  "Then blue 🔵": "Dan blauw 🔵",
  "Then yellow 🟡": "Dan geel 🟡",
  "This is a CIRCLE! Draw one big round circle ⭕": "Dit is een CIRKEL! Teken een grote ronde cirkel ⭕",
  "This is a CRESCENT! The curvy shape of the moon 🌙": "Dit is een HALVE MAAN! De gebogen vorm van de maan 🌙",
  "This is a DIAMOND! A square standing on its point 💎": "Dit is een RUIT! Een vierkant dat op zijn punt staat 💎",
  "This is a HEART! Draw two bumps and a point ❤️": "Dit is een HART! Teken twee bobbels en een punt ❤️",
  "This is a HEXAGON! Draw 6 straight sides ⬡": "Dit is een ZESHOEK! Teken 6 rechte zijden ⬡",
  "This is a PARALLELOGRAM! A rectangle that leans over ▱": "Dit is een PARALLELLOGRAM! Een rechthoek die scheef staat ▱",
  "This is a PENTAGON! Draw 5 straight sides ⬠": "Dit is een VIJFHOEK! Teken 5 rechte zijden ⬠",
  "This is a RECTANGLE! 2 long sides and 2 short sides ▭": "Dit is een RECHTHOEK! 2 lange zijden en 2 korte zijden ▭",
  "This is a SQUARE! Draw 4 sides that are all the same 🟦": "Dit is een VIERKANT! Teken 4 zijden die allemaal even lang zijn 🟦",
  "This is a STAR! Draw 5 pointy points ⭐": "Dit is een STER! Teken 5 puntige punten ⭐",
  "This is a TRAPEZIUM! Narrow on top, wide at the bottom ⏢": "Dit is een TRAPEZIUM! Smal boven, breed onderaan ⏢",
  "This is a TRIANGLE! Draw 3 straight sides 🔺": "Dit is een DRIEHOEK! Teken 3 rechte zijden 🔺",
  "This is an OCTAGON! Draw 8 straight sides 🛑": "Dit is een ACHTHOEK! Teken 8 rechte zijden 🛑",
  "This is an OVAL! Like a circle, but stretched 🥚": "Dit is een OVAAL! Als een cirkel, maar uitgerekt 🥚",
  "Trace the black border ⬛": "Trek de zwarte rand ⬛",
  "Trace the black borders ⬛": "Trek de zwarte randen ⬛",
  "Write some white marks across ✍️": "Schrijf wat witte streepjes overdwars ✍️",
  "Write the player's name — MBAPPÉ ⚽": "Schrijf de naam van de speler — MBAPPÉ ⚽",
  "Write the words \"Ordem e Progresso\" — \"Order and Progress\"! ✍️": "Schrijf de woorden \"Ordem e Progresso\" — \"Order and Progress\"! ✍️",
  "Write three little green marks ✍️": "Schrijf drie kleine groene streepjes ✍️",
};

/** Build the "Schrijf: B-O-O-M ✏️" hint for the name-writing final step.
 *  Long names use the `written` short form when available. */
export function nameHintNl(name: string, written?: string): string {
  const w = written || name;
  const letters = [...w.toUpperCase()].join("-");
  return `Schrijf: ${letters} ✏️`;
}

/** Spoken name aliases for voice control — the full set of English names
 *  work via fallback; these cover Dutch-only variations. */
export const ALIASES_NL: Record<string, string[]> = {
  "cloud": ["wolk"],
  "cloud-7": ["wolk"],
  "cloud-10": ["wolk"],
  "heromask": ["superstormy", "super-stormy", "stormy"],
  "heromask-7": ["superstormy", "super-stormy", "stormy"],
  "heromask-10": ["superstormy", "super-stormy", "stormy"],
  "popstar": ["popster"],
  "popstar-7": ["popster"],
  "popstar-10": ["popster"],
  "princess": ["prinses"],
  "princess-7": ["prinses"],
  "princess-10": ["prinses"],
  "jersey": ["shirt"],
  "ice-cream": ["ijsje"],
};
